import YAML from "yaml";
import { YAMLMap, Scalar } from "yaml/types";
import isEmpty from "lodash/isEmpty";
import { SPECTRO_TAG } from "./constants";

YAML.scalarOptions.null.nullStr = "";
YAML.scalarOptions.str.fold = {
  lineWidth: 0,
};
YAML.scalarOptions.str.defaultType = "QUOTE_DOUBLE";
YAML.scalarOptions.str.defaultKeyType = "PLAIN";

const presetsCommentRegex = /^(# spectrocloud.com\/enabled-presets:).*/g;

export function parseTagsForInput(tags) {
  return ((tags && Object.keys(tags)) || []).map((key) => {
    const value = tags[key];
    if (value !== SPECTRO_TAG) {
      return `${key}:${value}`;
    }

    return key;
  });
}

export function flattenObjectKeys(data = {}) {
  let flattenedObject = {};

  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (value && typeof value === "object") {
      let flatObject = flattenObjectKeys(value);
      Object.keys(flatObject).forEach((flatObjectKey) => {
        const flatObjectValue = flatObject[flatObjectKey];
        flattenedObject[key + "." + flatObjectKey] = flatObjectValue;
      });
    } else {
      flattenedObject[key] = value;
    }
  });

  return flattenedObject;
}

function createNewPathValue({ yamlDoc, pathArray = [], value }) {
  pathArray.forEach((key, index, array) => {
    const currentPath = array.slice(0, index);
    const nextPath = [...currentPath, key];
    const keyExists = yamlDoc.hasIn(nextPath);
    const isLast = index === array.length - 1;
    if (!keyExists) {
      const currentPathValue = yamlDoc.getIn(currentPath);
      const scalarKey = new Scalar(key);
      scalarKey.type = "PLAIN";
      if (!currentPathValue || !currentPathValue.items) {
        yamlDoc.setIn(currentPath, new YAMLMap());
      }
      yamlDoc.addIn(currentPath, {
        key: scalarKey,
        value: isLast ? value : new YAMLMap(),
      });
    }
  });
}

export function parseYAMLValues(values = "", presetValues) {
  if (!presetValues) {
    return;
  }

  try {
    const parsedYaml = YAML.parseDocument(values);
    const addValues = presetValues.add?.replace(/\u21b5/g, "\n") || "";
    const addJSON = YAML.parseDocument(addValues)?.toJSON() || {};
    const flattenedAdd = flattenObjectKeys(addJSON);
    const removePaths = presetValues.remove || [];

    if (Object.keys(flattenedAdd).length && !parsedYaml.contents) {
      parsedYaml.setIn([], new YAMLMap());
    }

    Object.keys(flattenedAdd).forEach((keyPath) => {
      const pathArray = keyPath.split(".");
      const keyExists = parsedYaml.hasIn(pathArray);
      if (!keyExists) {
        createNewPathValue({
          yamlDoc: parsedYaml,
          pathArray,
          value: flattenedAdd[keyPath],
        });
      } else {
        parsedYaml.setIn(pathArray, flattenedAdd[keyPath]);
      }
    });

    removePaths.forEach((path) => {
      const pathArray = path.split(".");
      const keyExists = parsedYaml.hasIn(pathArray);

      if (keyExists) {
        parsedYaml.deleteIn(pathArray);
        const parentPath = pathArray.slice(0, -1);
        const node = parsedYaml.getIn(parentPath);
        if (node?.items && node.items.length === 0) {
          parsedYaml.setIn(parentPath, null);
        }
      }
    });
    return parsedYaml.toString();
  } catch (err) {
    console.error(err);
  }
}

export function getDefaultPresetsFromValues(values = "") {
  const newValues = values?.replace(/\u21b5/g, "\n") || "";
  const comment = newValues.match(presetsCommentRegex)?.[0];
  const presetValues =
    comment && comment.trim().split("spectrocloud.com/enabled-presets:")?.[1];
  if (presetValues) {
    const groups = presetValues.split(",");
    return groups.reduce((acc, group) => {
      const [key, value] = group.trim().split(":");
      if (value) {
        return {
          ...acc,
          [key]: value.trim(),
        };
      }
      return {
        ...acc,
        emptyGroup: [...(acc.emptyGroup || []), key],
      };
    }, {});
  }
  return {};
}

export function getPackValuesWithoutPresetsComment(values = "") {
  const newValues = values?.replace(/\u21b5/g, "\n");
  return newValues?.replace(presetsCommentRegex, "") || "";
}

export function getPackValuesWithPresetsComment(values = "", presets) {
  if (isEmpty(presets)) {
    return values;
  }
  const enabledPresets = "# spectrocloud.com/enabled-presets: ";
  const presetValues = Object.keys(presets)
    .reduce((acc, key) => {
      if (!key) return acc;
      if (key === "emptyGroup") {
        return [...acc, ...(presets[key] || [])];
      }
      return [...acc, `${key}:${presets[key]}`];
    }, [])
    .join(",");
  const comment = `${enabledPresets}${presetValues}`;
  return `${comment}\n${values}`;
}
